// useTableModalActions.js

import { useCallback } from 'react';

import { useUser } from 'context/UserContext';

import { updateNode } from 'services/nodes/NodeWriteService';

import { isNodelistAllowMultiple, normalizeNodelistValue } from 'utilities/NodelistField';
import { getDataSectionName } from 'views/table/utils/tableFieldUtils';

const useTableModalActions = ({ modalState, typeDefKey }) => {
    const { user } = useUser();

    const clampPercentage = (value) => {
        const numeric = Number(value);
        if (!Number.isFinite(numeric)) return 0;
        if (numeric < 0) return 0;
        if (numeric > 100) return 100;
        return Math.round(numeric);
    };

    const handleEnumChange = useCallback(async (nextValue) => {
        const { node, fieldDef } = modalState || {};
        if (!node?.key || !fieldDef) return;
        const dataKey = getDataSectionName(typeDefKey);
        if (!dataKey) return;
        const fieldName = fieldDef?.name;
        if (!fieldName) return;
        const dataObj = node?.[dataKey] && typeof node[dataKey] === 'object'
            ? node[dataKey]
            : {};
        try {
            await updateNode(node.key, {
                [dataKey]: {
                    ...dataObj,
                    [fieldName]: nextValue ?? null,
                },
            }, { actor: user });
        } catch (error) {
            console.error('[TableView] update enum error:', error);
        }
    }, [modalState, typeDefKey, user]);

    const handleDateChange = useCallback(async (nextValue) => {
        const { node, fieldDef } = modalState || {};
        if (!node?.key || !fieldDef) return;
        const dataKey = getDataSectionName(typeDefKey);
        if (!dataKey) return;
        const fieldName = fieldDef?.name;
        if (!fieldName) return;
        const dataObj = node?.[dataKey] && typeof node[dataKey] === 'object'
            ? node[dataKey]
            : {};
        try {
            await updateNode(node.key, {
                [dataKey]: {
                    ...dataObj,
                    [fieldName]: nextValue ?? null,
                },
            }, { actor: user });
        } catch (error) {
            console.error('[TableView] update date error:', error);
        }
    }, [modalState, typeDefKey, user]);

    const handleTagsSaved = useCallback(async (nextTags) => {
        const { node } = modalState || {};
        if (!node?.key) return;
        const tags = Array.isArray(nextTags) ? nextTags : [];
        try {
            await updateNode(node.key, { tags }, { actor: user });
        } catch (error) {
            console.error('[TableView] update tags error:', error);
        }
    }, [modalState, user]);

    const handleLocationSaved = useCallback(async (dataLocation) => {
        const { node, fieldDef } = modalState || {};
        if (!node?.key || !fieldDef) return;

        const dataKey = getDataSectionName(typeDefKey);
        if (!dataKey) return;
        const fieldName = fieldDef?.name;
        if (!fieldName) return;

        const dataObj = node?.[dataKey] && typeof node[dataKey] === 'object'
            ? node[dataKey]
            : {};

        try {
            await updateNode(node.key, {
                [dataKey]: {
                    ...dataObj,
                    [fieldName]: dataLocation ?? null,
                },
            }, { actor: user });
        } catch (error) {
            console.error('[TableView] update location error:', error);
        }
    }, [modalState, typeDefKey, user]);

    const handlePeopleSaved = useCallback(async (nextPeople) => {
        const { node, fieldDef } = modalState || {};
        if (!node?.key || !fieldDef) return;
        const dataKey = getDataSectionName(typeDefKey);
        if (!dataKey) return;
        const fieldName = fieldDef?.name;
        if (!fieldName) return;

        const people = Array.isArray(nextPeople) ? nextPeople : [];
        const dataObj = node?.[dataKey] && typeof node[dataKey] === 'object'
            ? node[dataKey]
            : {};

        try {
            await updateNode(node.key, {
                [dataKey]: {
                    ...dataObj,
                    [fieldName]: people,
                },
            }, { actor: user });
        } catch (error) {
            console.error('[TableView] update people error:', error);
        }
    }, [modalState, typeDefKey, user]);

    const handleNodelistSaved = useCallback(async (nextKeys) => {
        const { node, fieldDef } = modalState || {};
        if (!node?.key || !fieldDef) return;
        const dataKey = getDataSectionName(typeDefKey);
        if (!dataKey) return;
        const fieldName = fieldDef?.name;
        if (!fieldName) return;

        const keys = normalizeNodelistValue(nextKeys, {
            allowMultiple: isNodelistAllowMultiple(fieldDef),
        });
        const dataObj = node?.[dataKey] && typeof node[dataKey] === 'object'
            ? node[dataKey]
            : {};

        try {
            await updateNode(node.key, {
                [dataKey]: {
                    ...dataObj,
                    [fieldName]: keys,
                },
            }, { actor: user });
        } catch (error) {
            console.error('[TableView] update nodelist error:', error);
        }
    }, [modalState, typeDefKey, user]);

    const handlePercentageChange = useCallback(async (nextValue) => {
        const { node, fieldDef } = modalState || {};
        if (!node?.key || !fieldDef) return;
        const dataKey = getDataSectionName(typeDefKey);
        if (!dataKey) return;
        const fieldName = fieldDef?.name;
        if (!fieldName) return;

        const dataObj = node?.[dataKey] && typeof node[dataKey] === 'object'
            ? node[dataKey]
            : {};

        try {
            await updateNode(node.key, {
                [dataKey]: {
                    ...dataObj,
                    [fieldName]: clampPercentage(nextValue),
                },
            }, { actor: user });
        } catch (error) {
            console.error('[TableView] update percentage error:', error);
        }
    }, [modalState, typeDefKey, user]);

    return {
        handleDateChange,
        handleEnumChange,
        handleTagsSaved,
        handleLocationSaved,
        handlePeopleSaved,
        handleNodelistSaved,
        handlePercentageChange,
    };
};

export default useTableModalActions;
